import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Collapse,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Home as HomeIcon,
  AccessTime as AccessTimeIcon,
  History as HistoryIcon,
  Assessment as AssessmentIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  ExpandLess,
  ExpandMore,
  EventNote as EventNoteIcon,
  Info as InfoIcon,
  WarningAmber as WarningIcon,
  CalendarMonth as CalendarMonthIcon,
  CheckCircle as CheckCircleIcon,
  Group as GroupIcon,
  Download as DownloadIcon,
  PlaylistAddCheck as PlaylistAddCheckIcon,
  Campaign as CampaignIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useEffect, useState } from 'react';
import { adminGroupsAPI, guidedPresenceAPI } from '../services/api';

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isImpersonating, checkImpersonation } = useAuth();
  const { isAdmin, isSuperAdmin: isSuperAdminFromHook, isGroupAdmin, isImpersonating: isImpersonatingFromHook } = usePermissions();
  
  // Ascolta i cambiamenti di localStorage (per tab multiple o quando si avvia/termina impersonazione)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'impersonated_user_id' || e.key === 'view_admin_mode') {
        checkImpersonation();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []); // Solo al mount
  
  // Controlla se siamo in modalità "vedi admin" (per vedere temporaneamente l'area admin durante l'impersonazione)
  const viewAdminMode = localStorage.getItem('view_admin_mode') === 'true';
  
  // Controlla direttamente localStorage come fallback per isImpersonating
  const impersonatedUserId = localStorage.getItem('impersonated_user_id') || localStorage.getItem('impersonated_user_id_backup');
  const actuallyImpersonating = isImpersonating || (!!impersonatedUserId && !viewAdminMode);
  
  // Se si sta impersonando, usa i permessi dell'utente impersonato
  // Se non si sta impersonando, usa i permessi dell'utente originale
  const effectiveRole = user?.role;
  
  // Usa usePermissions per coerenza - durante l'impersonazione, isSuperAdmin è sempre false
  // Se siamo in modalità "vedi admin", mostriamo i menu admin del super admin originale
  // Ma usePermissions già gestisce l'impersonazione, quindi usiamo direttamente i valori dall'hook
  const isAdminToUse = viewAdminMode ? (effectiveRole === 'admin') : isAdmin;
  const isSuperAdminToUse = viewAdminMode ? (user?.is_super_admin === true) : isSuperAdminFromHook;
  const isGroupAdminToUse = viewAdminMode ? isGroupAdmin : isGroupAdmin;
  const adminDashboardPath = isGroupAdminToUse && !isSuperAdminToUse ? '/group-dashboard' : '/dashboard';
  const adminDashboardLabel = isGroupAdminToUse && !isSuperAdminToUse ? 'Dashboard Gruppo' : 'Dashboard Centrale';
  const sidebarSubtitle = isSuperAdminToUse
    ? 'Controllo centrale e supervisione'
    : isGroupAdminToUse
      ? 'Operativita e controllo di gruppo'
      : 'Area personale presenze';
  const [isInAdminManagedGroup, setIsInAdminManagedGroup] = useState<boolean>(false);
  const [loadingAdminManagedCheck, setLoadingAdminManagedCheck] = useState(false);
  const [hasAdminManagedGroups, setHasAdminManagedGroups] = useState<boolean>(false);
  
  const [openSections, setOpenSections] = React.useState<{[key: string]: boolean}>({
    presenze: true,
    cartellini: false,
    ferie: false,
    dashboard: true, // Apri di default Dashboard e Monitoraggio
    personale: false,
    admin_cartellini: false,
    admin_export: false,
    admin_ferie: false,
    superadmin: false,
  });
  
  // Verifica se l'utente appartiene a un gruppo con gestione admin
  useEffect(() => {
    let cancelled = false;
    
    const checkAdminManagedGroup = async () => {
      if (!user || effectiveRole !== 'employee') {
        if (!cancelled) setIsInAdminManagedGroup(false);
        return;
      }
      
      try {
        if (!cancelled) setLoadingAdminManagedCheck(true);
        const response = await adminGroupsAPI.isEmployeeInAdminManagedGroup();
        if (!cancelled) {
          setIsInAdminManagedGroup(response.is_in_admin_managed_group || false);
        }
      } catch (err) {
        console.error('[Sidebar] Errore verifica gruppo con gestione admin:', err);
        if (!cancelled) setIsInAdminManagedGroup(false);
      } finally {
        if (!cancelled) setLoadingAdminManagedCheck(false);
      }
    };
    
    if (user?.id) {
      checkAdminManagedGroup();
    } else {
      setIsInAdminManagedGroup(false);
    }

    const onFocus = () => {
      checkAdminManagedGroup();
    };
    window.addEventListener('focus', onFocus);
    
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
    };
  }, [user?.id, effectiveRole]);
  // Verifica se l'admin di gruppo ha gruppi con admin_manages_timesheets
  useEffect(() => {
    let cancelled = false;
    const checkManagedGroups = async () => {
      if (!isAdminToUse || !user?.id) {
        if (!cancelled) setHasAdminManagedGroups(false);
        return;
      }
      try {
        const groups = await guidedPresenceAPI.getManagedGroups();
        if (!cancelled) setHasAdminManagedGroups(groups && groups.length > 0);
      } catch {
        if (!cancelled) setHasAdminManagedGroups(false);
      }
    };
    checkManagedGroups();
    return () => { cancelled = true; };
  }, [user?.id, isAdminToUse]);

  const [changelogOpen, setChangelogOpen] = React.useState(false);

  const handleToggle = (section: string) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Menu items per tutti gli utenti
  const commonMenuItemsBase = [
    {
      title: 'Monitoraggio presenze',
      icon: <AccessTimeIcon />,
      key: 'presenze',
      items: [
        // Timbratura Mobile nascosta (route /mobile-presence e MobilePresence restano per eventuale reinclusione)
        { text: 'Area personale', icon: <PersonIcon />, path: '/employee-dashboard' }
      ]
    },
    {
      title: 'Cartellini',
      icon: <HistoryIcon />,
      key: 'cartellini',
      items: [
        // Nascondi "Cartellino mensile" per:
        // 1. Utenti con ruolo "employee"
        // 2. Dipendenti che appartengono a un gruppo con gestione admin
        ...(effectiveRole !== 'employee' && !isInAdminManagedGroup ? [{ text: 'Cartellino mensile', icon: <AssessmentIcon />, path: '/timesheet' }] : []),
        { text: 'Validazione presenze', icon: <CheckCircleIcon />, path: '/presence-validation' }
      ]
    },
    {
      title: 'Ferie e Permessi',
      icon: <EventNoteIcon />,
      key: 'ferie',
      items: [
        { text: 'Le mie richieste', icon: <EventNoteIcon />, path: '/leave' }
      ]
    }
  ];

  // Filtra le sezioni vuote (se una sezione non ha items, rimuovila)
  const commonMenuItems = commonMenuItemsBase.filter(section => section.items.length > 0);

  // Menu items solo per admin
  const adminMenuItems = [
    {
      title: 'Dashboard e Monitoraggio',
      icon: <HomeIcon />,
      key: 'dashboard',
      items: [
        { text: adminDashboardLabel, icon: <HomeIcon />, path: adminDashboardPath },
        { text: 'Anomalie e controlli', icon: <WarningIcon />, path: '/anomalies' }
      ]
    },
    {
      title: 'Organizzazione e Utenti',
      icon: <PeopleIcon />,
      key: 'personale',
      items: [
        { text: 'Gestione dipendenti', icon: <PeopleIcon />, path: '/employees' },
        { text: 'Dipendenti eliminati', icon: <PeopleIcon />, path: '/employees/deleted' },
        { text: 'Gestione utenti', icon: <PersonIcon />, path: '/users' },
        { text: 'Struttura gruppi e deleghe', icon: <GroupIcon />, path: '/admin-groups' }
      ]
    },
    {
      title: 'Presenze e Cartellini',
      icon: <CalendarMonthIcon />,
      key: 'admin_cartellini',
      items: [
        { text: 'Inserimento presenze gruppo', icon: <PlaylistAddCheckIcon />, path: '/guided-presence' },
        { text: 'Cartellini del gruppo', icon: <CalendarMonthIcon />, path: '/admin-timesheets' },
        { text: 'Anomalie e controlli', icon: <WarningIcon />, path: '/anomalies' },
        { text: 'Validazioni presenze', icon: <CheckCircleIcon />, path: '/presence-validation' },
        { text: 'Gestione validazioni', icon: <AssessmentIcon />, path: '/timesheets-management' },
      ]
    },
    {
      title: 'Export e Report',
      icon: <DownloadIcon />,
      key: 'admin_export',
      items: [
        { text: 'Report e analisi', icon: <AssessmentIcon />, path: '/reports' },
        { text: 'Esportazione massiva cartellini', icon: <DownloadIcon />, path: '/timesheets-mass-export' },
        { text: 'Export presenze mensili', icon: <DownloadIcon />, path: '/reports/monthly-schedule' }
      ]
    },
    {
      title: 'Ferie e Permessi',
      icon: <EventNoteIcon />,
      key: 'admin_ferie',
      items: [
        { text: 'Storico ferie e permessi', icon: <HistoryIcon />, path: '/leave/history' },
        { text: 'Voci ferie e permessi', icon: <EventNoteIcon />, path: '/leave/reasons' },
        { text: 'Import saldi ferie', icon: <EventNoteIcon />, path: '/leave/import' },
        { text: 'Quadro saldi ferie', icon: <EventNoteIcon />, path: '/leave/balances' }
      ]
    }
  ];

  // Menu items solo per super admin (solo se NON si sta impersonando)
  const superAdminMenuItems = isSuperAdminToUse && !actuallyImpersonating ? [
    {
      title: 'Supervisione Avanzata',
      icon: <PersonIcon />,
      key: 'superadmin',
      items: [
        { text: 'Accesso come altro utente', icon: <PersonIcon />, path: '/impersonation' },
        { text: 'Annunci globali', icon: <CampaignIcon />, path: '/announcements' }
      ]
    }
  ] : [];

  // Combina menu items
  const allMenuItems = [...adminMenuItems, ...superAdminMenuItems];

  // Menu items parziali per admin di gruppo (solo esportazione massiva)
  // Sezione inserimento presenze guidato (visibile ad admin di gruppo con admin_manages_timesheets)
  const guidedPresenceItems = hasAdminManagedGroups ? [
    {
      title: 'Operativita di Gruppo',
      icon: <PlaylistAddCheckIcon />,
      key: 'admin_cartellini',
      items: [
        { text: 'Inserimento presenze gruppo', icon: <PlaylistAddCheckIcon />, path: '/guided-presence' },
        { text: 'Cartellini del gruppo', icon: <CalendarMonthIcon />, path: '/admin-timesheets' },
        { text: 'Validazioni del gruppo', icon: <CheckCircleIcon />, path: '/presence-validation' },
      ]
    }
  ] : [];

  const groupAdminExportOnlyItems = [
    {
      title: 'Report di Gruppo',
      icon: <DownloadIcon />,
      key: 'admin_export',
      items: [
        { text: 'Esportazione massiva cartellini', icon: <DownloadIcon />, path: '/timesheets-mass-export' }
      ]
    },
    {
      title: 'Ferie e Permessi',
      icon: <EventNoteIcon />,
      key: 'admin_ferie',
      items: [
        { text: 'Storico ferie e permessi', icon: <HistoryIcon />, path: '/leave/history' },
        { text: 'Voci ferie e permessi', icon: <EventNoteIcon />, path: '/leave/reasons' },
      ]
    }
  ];

  // Combina menu items in base al ruolo
  // REGOLA:
  // - Super admin: vede tutti i menu admin
  // - Admin di gruppo (non super admin): vede export + inserimento presenze (se ha gruppi gestiti)
  // - Durante impersonazione: stessa logica basata sul ruolo dell'utente impersonato
  let menuItems = commonMenuItems;
  
  if (isAdminToUse) {
    // Se è super admin, mostra tutti i menu admin
    if (isSuperAdminToUse) {
      menuItems = allMenuItems;
    } else {
      // Admin di gruppo: export + inserimento presenze (se ha gruppi con admin_manages_timesheets)
      menuItems = [...commonMenuItems, ...guidedPresenceItems, ...groupAdminExportOnlyItems];
    }
  }

  const isActive = (path: string) => location.pathname === path;

  return (
    <Box 
      sx={{ 
        width: 280,
        backgroundColor: '#f5f5f5',
        borderRight: '1px solid #e0e0e0',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        height: '100%'
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
        <Typography 
          variant="h6" 
          sx={{ 
            color: '#0d47a1',
            fontWeight: 'bold',
            mb: 1
          }}
        >
          Monitoraggio Presenze
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {sidebarSubtitle}
        </Typography>
      </Box>

      {/* Breadcrumb */}
      <Box sx={{ px: 2, mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Sei in: {getCurrentSection(location.pathname)}
        </Typography>
      </Box>

      {/* Menu Items */}
      <Box sx={{ flexGrow: 1, overflow: 'auto', pb: 8 }}>
        {menuItems.map((section) => {
          const sectionKey = section.key || section.title || JSON.stringify(section.items);
          return (
            <React.Fragment key={sectionKey}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => section.key && handleToggle(section.key)}
                  sx={{ px: 2 }}
                >
                  <ListItemIcon>{section.icon}</ListItemIcon>
                  <ListItemText primary={section.title} />
                  {section.key && (openSections[section.key] ? <ExpandLess /> : <ExpandMore />)}
                </ListItemButton>
              </ListItem>

              {section.key && (
                <Collapse in={openSections[section.key]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {section.items
                      .filter((item: any) => {
                        // Filtra "Gestione Gruppi Admin" solo per super admin
                        if (item.path === '/admin-groups' && !isSuperAdminToUse) {
                          return false;
                        }
                        return true;
                      })
                      .map((item: any) => (
                      <ListItemButton
                        key={item.text}
                        onClick={() => navigate(item.path)}
                        sx={{
                          pl: 4,
                          backgroundColor: isActive(item.path) ? '#e3f2fd' : 'transparent',
                          '&:hover': { backgroundColor: '#f0f8ff' }
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {item.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={item.text}
                          sx={{
                            color: isActive(item.path) ? '#0d47a1' : 'inherit',
                            fontWeight: isActive(item.path) ? 'bold' : 'normal'
                          }}
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </Collapse>
              )}

              <Divider />
            </React.Fragment>
          );
        })}
      </Box>

      {/* Version Footer - Fixed at bottom (solo admin, non employee) */}
      {isAdmin && effectiveRole !== 'employee' && (
        <Box 
          sx={{ 
            borderTop: '2px solid #0d47a1',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: '#e3f2fd',
            zIndex: 100,
          }}
        >
          <ListItemButton
            onClick={() => setChangelogOpen(true)}
            sx={{ 
              minHeight: '60px',
              '&:hover': {
                backgroundColor: 'rgba(13, 71, 161, 0.05)'
              }
            }}
          >
            <InfoIcon sx={{ color: '#0d47a1', fontSize: '1.2rem', mr: 2 }} />
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#0d47a1',
                fontWeight: 600,
                fontSize: '0.875rem'
              }}
            >
              Versione 1.8.0
            </Typography>
            <Chip
              label="Novità"
              size="small" 
              color="primary" 
              sx={{ ml: 'auto', fontWeight: 'bold' }}
            />
          </ListItemButton>
        </Box>
      )}

      {/* Changelog Dialog */}
      {isAdmin && effectiveRole !== 'employee' && (
        <Dialog 
          open={changelogOpen} 
          onClose={() => setChangelogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" gap={2}>
              <InfoIcon color="primary" />
              <Typography variant="h6">Changelog</Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
          {/* v1.8.0 */}
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mb: 2 }}>
            Changelog v1.8.0 (04/11/2026)
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Entrata solo dalle 9"
                secondary="Prima delle 9 il pulsante di entrata non si preme. Se entri entro 10 minuti, l'orario vale completo. Se superi i 10 minuti, i minuti di ritardo restano a debito."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Doppia timbratura per la pausa pranzo"
                secondary="Si accende per un gruppo o per un singolo operatore. Quattro timbrature: entra, esce a pranzo, rientra, esce. La pausa è quella timbrata, i minuti del contratto non si tolgono un'altra volta."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Due modi di vedere il cartellino"
                secondary="Per gruppo o per operatore si sceglie se in validazione si vedono solo le ore oppure anche gli orari. Chi timbra vede entrata e uscita. Con la doppia timbratura vede anche il rientro e l'uscita finale."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Chi ha convalidato compare nell'Excel"
                secondary="Nel file normale e nel file INPAS, quando il cartellino è già convalidato, c'è la riga con nome e giorno della convalida."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Il cambio tipo di presenza si vede subito"
                secondary="Passando dalla presenza giornaliera all'entrata e uscita, o il contrario, la pagina si aggiorna senza doverla ricaricare."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Cartellini massivi in un unico zip"
                secondary="Lo scarico di più cartellini, normali o INPAS, arriva in un solo file zip con un cartellino per persona."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          {/* v1.7.1 */}
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mb: 2 }}>
            Changelog v1.7.1 (02/07/2026)
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Import saldi ferie direttamente dall'estratto paghe"
                secondary="L'importazione dei saldi (ferie, permessi, ex festività) ora accetta anche il file così come esce dal programma paghe, con una riga per ogni voce: il sistema lo riconosce da solo e lo raggruppa per dipendente tramite il codice fiscale. Non serve più rielaborare il file a mano; il vecchio tracciato a colonne resta comunque valido. Le voci non gestite (es. Banca ore) vengono ignorate."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          {/* v1.7.0 */}
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mb: 2 }}>
            Changelog v1.7.0 (02/07/2026)
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Ex Festività richiedibili come assenza a ore"
                secondary="Le ex festività sono ora tra i tipi di richiesta disponibili (per dipendenti e amministratori) e si richiedono in ore come i permessi. Il contatore delle ex festività resta visibile nel saldo ferie/permessi."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Orari giornalieri personalizzati (anche oltre 40 ore settimanali)"
                secondary="Nuovo interruttore nella scheda dipendente: se attivo, le ore attese di ogni giorno vengono calcolate dagli orari giornalieri (es. 9h il lunedì, 4,5h il martedì) invece che dalla media settimanale. Cartellini, anomalie e assenze seguono l'orario del giorno."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Pausa pranzo corretta per i part-time"
                secondary="La pausa è sempre 0 per i contratti fino a 20 ore settimanali (applicato automaticamente anche ai dipendenti esistenti). Inoltre, modificando la pausa di un dipendente, le ore già registrate nel mese vengono ricalcolate: la vecchia pausa non resta più scalata sui giorni già inseriti."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Eliminazione della timbratura di un singolo giorno"
                secondary="In Inserimento presenze gruppo è ora possibile rimuovere l'orario inserito giorno per giorno (icona cestino accanto agli orari), senza dover eliminare tutte le presenze del mese."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Inserimento presenze gruppo: visibili tutti i gruppi"
                secondary="Il super admin ora vede tutti i gruppi, anche quelli senza l'opzione 'admin gestisce i cartellini': più facile gestire i gruppi privi di un admin di riferimento."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Esportazione massiva cartellini con filtro per gruppo"
                secondary="Nuovo selettore 'Gruppo' nell'esportazione massiva: consente di esportare tutti i cartellini (validati e non validati) dei dipendenti di un determinato gruppo."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Import Excel: assegnazione automatica al gruppo"
                secondary="Nuova colonna opzionale 'Gruppo' nel file di import: l'operatore importato viene aggiunto automaticamente al proprio gruppo in Struttura gruppi e deleghe (vale anche per i dipendenti già esistenti)."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Ricerca membri in Struttura gruppi e deleghe"
                secondary="Nuovo campo di ricerca per nome e cognome nella lista dei dipendenti del gruppo, per individuare e rimuovere più facilmente un utente."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Fix ricerca in Gestione Dipendenti"
                secondary="La casella di ricerca (cognome, nome, codice fiscale, matricola) non perde più il focus durante l'aggiornamento dei risultati: si può digitare senza doverla riselezionare."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Esito invio email credenziali visibile all'admin"
                secondary="Quando si abilitano utenti o si inviano credenziali temporanee, l'esito dell'invio email è ora mostrato per ciascun utente: se un invio fallisce, l'admin viene avvisato e può comunicare le credenziali manualmente."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          {/* v1.6.3 */}
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mb: 2 }}>
            Changelog v1.6.3 (16/06/2026)
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Permesso scalato dalle ore lavorate, non più sommato"
                secondary="Quando si inserisce un permesso su una giornata già timbrata, le ore di permesso vengono sottratte dalle ore di presenza (con tetto alle ore attese) invece di sommarsi: il totale del giorno resta corretto (es. 8h + 2h di permesso = 8h, non 10h)."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Ferie e malattia ora sempre registrate e visibili negli export"
                secondary="Corretto un problema per cui ferie e malattia inserite dagli amministratori potevano non essere scritte nel calendario assenze e quindi non comparire nei riepiloghi Excel. Ora vengono registrate correttamente, come già avveniva per i permessi."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Niente più timbrature residue sui giorni di assenza a giornata intera"
                secondary="Assegnando ferie o malattia a giornata intera, l'eventuale presenza già inserita per quel giorno viene rimossa automaticamente: nei riepiloghi prevale l'assenza, senza doppioni che nascondano l'icona F/M."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Registrazione rapida con un solo pulsante per gli operatori a orario ridotto"
                secondary="Gli operatori con al massimo 5 ore al giorno non devono più fare entrata e uscita separate: un unico pulsante 'Registra presenza di oggi' compila l'intera giornata prevista con un clic, anche da smartphone."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          {/* v1.6.2 */}
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mb: 2 }}>
            Changelog v1.6.2 (23/03/2026)
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Policy export cartellino applicata anche in impersonazione e validazione"
                secondary="L'esportazione Excel del cartellino rispetta ora la policy globale/gruppo anche durante impersonazione e nella schermata di validazione presenze, con fallback restrittivo in caso di errore API."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Inserimento Presenze di Gruppo: stato ferie/permessi persistente"
                secondary="Corretto il mantenimento dello stato del dropdown tipo giornata, con preservazione durante navigazione giornaliera/settimanale, refresh e reload pagina tramite bozza locale per gruppo."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Applica tutti esteso anche alla vista mensile di guided-presence"
                secondary="Aggiunta azione mensile per applicare il template orario su tutto il periodo, evitando sovrascrittura di assenze o presenze già registrate e rispettando weekend/festivi."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Assenze admin allineate tra Presenze Gruppo, cartellino e validazione"
                secondary="Introdotto fallback da leave_requests approvate quando leave_calendar è incompleta, così malattia/permessi inseriti dagli admin gruppo risultano visibili in tutti i moduli."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          {/* v1.6.1 */}
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mb: 2 }}>
            Changelog v1.6.1 (16/03/2026)
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Inserimento presenze di gruppo riallineato ai dipendenti effettivamente esposti"
                secondary="La selezione dei dipendenti, i conteggi operativi e le azioni massive nell'inserimento presenze di gruppo sono ora coerenti con il perimetro realmente visualizzato, evitando disallineamenti tra gruppo selezionato e dipendenti presenti in tabella."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Dettagli di ferie, permessi e malattia mantenuti correttamente dopo il salvataggio"
                secondary="Nell'inserimento presenze di gruppo il dato salvato viene ora riletto e mostrato correttamente anche in visualizzazione successiva, mantenendo tipologia assenza, ore e causale nei campi dettaglio senza lasciare i trattini."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Export Presenze Mensili esteso con esportazione massiva INPAS"
                secondary="Alla schermata di export mensile e stato aggiunto anche il download massivo dei cartellini regolari in formato INPAS, riutilizzando una logica condivisa per mantenere formato e contenuti coerenti tra gli strumenti di esportazione."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Header sticky nella gestione dipendenti admin"
                secondary="L'intestazione della tabella dipendenti rimane ora sempre visibile durante lo scorrimento verticale, migliorando la leggibilita dell'elenco e il lavoro operativo su tabelle estese."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Rimozione automatica dei dipendenti eliminati dai gruppi"
                secondary="Quando un dipendente viene eliminato, il sistema lo rimuove anche dalle assegnazioni ai gruppi admin e aggiorna liste e conteggi in modo che i gruppi mostrino solo dipendenti ancora validi e attivi."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          {/* v1.6.0 */}
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mb: 2 }}>
            Changelog v1.6.0 (10/03/2026)
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Maggiore coerenza nei cartellini e nei conteggi"
                secondary="Le logiche di calcolo di ore lavorate, ore attese, straordinari, assenze e anomalie sono state uniformate tra cartellino dipendente, strumenti amministrativi e validazione mensile, con un miglioramento concreto dell'affidabilità complessiva."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Gestione storica delle variazioni orarie"
                secondary="Le modifiche a ore settimanali, giorni lavorativi e pausa pranzo vengono ora tracciate nel tempo. Il sistema applica automaticamente la regola corretta al periodo di riferimento, evitando alterazioni retroattive sui mesi già consolidati."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Validazioni mensili allineate alle modifiche successive"
                secondary="Quando una presenza, un'assenza approvata o una regola oraria viene modificata dopo la validazione del mese, la validazione viene revocata automaticamente. Questo garantisce che un cartellino validato resti sempre coerente con i dati effettivi."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="🔧" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Rafforzati i controlli di integrità sui dati"
                secondary="Sono stati introdotti vincoli e controlli automatici per impedire stati incoerenti, come timbrature aperte duplicate, orari non validi o valori negativi. È stata inoltre aggiunta una sanificazione automatica dei dati storici anomali per garantire l'avvio corretto del sistema."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Operatività più chiara per gli admin di gruppo"
                secondary="Viste, conteggi e funzioni amministrative sono stati riallineati al perimetro reale dei gruppi assegnati. Gli admin di gruppo operano quindi con dipendenti, cartellini e saldi ferie coerenti con il proprio ambito di responsabilità."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="🔧" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Migliorata la stabilità operativa della piattaforma"
                secondary="Sono stati aggiunti test automatici sui casi più critici del dominio orario e ridotti numerosi log tecnici non necessari nelle aree amministrative. L'applicazione risulta ora più stabile, più pulita e più prevedibile nell'utilizzo quotidiano."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          {/* v1.5.4 */}
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mb: 2 }}>
            Changelog v1.5.4 (02/02/2026)
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Gruppi con Gestione Cartellini Admin"
                secondary="Nuova funzionalità per creare gruppi admin speciali dove l'amministratore compila i cartellini per conto dei dipendenti associati. Durante la creazione o modifica di un gruppo, è possibile attivare l'opzione 'Admin gestisce i cartellini' che abilita questa modalità di gestione"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Attivazione Dipendenti senza Email"
                secondary="I dipendenti associati a gruppi con gestione cartellini admin possono essere attivati normalmente ma non ricevono l'email di attivazione automatica. Le credenziali vengono comunque generate e sono disponibili per l'amministratore di riferimento"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Area Riservata Limitata per Dipendenti"
                secondary="I dipendenti di gruppi con gestione cartellini admin hanno accesso limitato alla loro area riservata: possono solo esportare il foglio presenze ma non possono accedere al cartellino mensile. Il menu 'Cartellino mensile' viene automaticamente nascosto per questi utenti"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Indicatore Visivo Gruppi Speciali"
                secondary="I gruppi con gestione cartellini admin sono identificati con un badge 'Admin gestisce cartellini' nella lista dei gruppi, facilitando l'identificazione rapida di questi gruppi speciali"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="🔧" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Fix Importazione Saldi Ferie - Colonna ex_festivita_ore"
                secondary="Aggiunta migration automatica per creare la colonna 'ex_festivita_ore' nella tabella leave_balance se non esiste. Risolve gli errori durante l'importazione dei saldi ferie da Excel quando la colonna non era presente nel database"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Protezione contro Duplicati Importazione Saldi"
                secondary="Il sistema ora garantisce automaticamente che non vengano creati record duplicati durante l'importazione dei saldi ferie. Se si importa lo stesso dipendente per lo stesso anno più volte, i dati vengono aggiornati invece di creare duplicati grazie al vincolo UNIQUE e alla funzione UPSERT"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          {/* v1.5.3 */}
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mb: 2 }}>
            Changelog v1.5.3 (16/01/2026)
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Export Presenze Mensili con Ore Lavorate"
                secondary="Nuovo sistema di export che genera un file Excel separato per ogni dipartimento contenente le ore lavorate giornaliere di ciascun dipendente. Le ore visualizzate sono limitate al tetto massimo contrattuale previsto (ore_settimanali / giorni_settimanali) seguendo la logica di validazione presenze"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Export Raggruppato per Dipartimento in ZIP"
                secondary="L'export genera automaticamente un file ZIP contenente un Excel per ogni dipartimento (es: IT_2025-01.xlsx, Amministrazione_2025-01.xlsx). Ogni file include solo i dipendenti del rispettivo dipartimento con evidenziazione dei weekend in giallo"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Arrotondamento Ore Senza Decimali"
                secondary="Le ore lavorate nell'export vengono visualizzate come numeri interi arrotondati matematicamente (es: 7.2h → 7, 7.5h → 8), rendendo il report più pulito e leggibile per l'elaborazione"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Limitazione Ore al Tetto Massimo Contrattuale"
                secondary="Se un dipendente lavora più ore del previsto, l'export mostra solo il numero massimo di ore contrattuali. Il sistema applica la stessa logica utilizzata nella validazione presenze per garantire coerenza dei dati"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="🔧" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Fix Arrotondamento Orario di Uscita Export Massivo"
                secondary="Corretto l'arrotondamento dell'orario di uscita nell'esportazione massiva cartellini: l'arrotondamento non avviene mai per eccesso, garantendo precisione nei calcoli orari esportati"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="🔧" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Fix Salvataggio Orari Corretti"
                secondary="Risolto errore durante il salvataggio degli orari corretti manualmente dall'amministratore. Il sistema ora gestisce correttamente la modifica e memorizzazione degli orari nel database"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="🔧" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Fix Visualizzazione Operatori Scaduti"
                secondary="Corretto il problema di visualizzazione degli operatori con contratto scaduto che non apparivano correttamente nelle liste. Gli operatori scaduti sono ora visibili con il corretto stato contrattuale"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="🔧" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Fix Visualizzazione Dipendenti"
                secondary="Risolti problemi di visualizzazione per specifici dipendenti (es. Quadri Andrea) che presentavano anomalie nella rappresentazione dei dati nel sistema"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Gestione Ferie e Permessi"
                secondary="Implementato sistema completo di gestione ferie e permessi con import da template Excel aggiornato tramite 'Import Saldi Ferie'. Gli operatori visualizzano la rendicontazione dettagliata direttamente dalla dashboard con saldi aggiornati, ferie maturate, permessi residui e storico utilizzo"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Ottimizzazione Template Export Massivo"
                secondary="Aggiornato il template di esportazione massiva cartellini in base alle nuove specifiche fornite, migliorando formato e compatibilità con i sistemi di elaborazione dati"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          {/* v1.5.2 */}
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mb: 2 }}>
            Changelog v1.5.2 (03/01/2026)
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Rimozione Menu Check-in/Check-out"
                secondary="Rimosso il menu 'Check-in/Check-out' e la relativa route '/presence' dal sistema. La funzionalità di timbratura rimane disponibile tramite 'Timbratura Mobile' e 'Dashboard Dipendente'"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Semplificazione Card Dashboard Dipendente"
                secondary="Rimossi i campi dipartimento, qualifica, contratto, orario inizio e orario fine dalle card informative della dashboard dipendente per una visualizzazione più essenziale"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Restrizione Accesso Cartellino Mensile"
                secondary="Gli utenti con ruolo 'employee' non possono più accedere al cartellino mensile. Il menu è nascosto e l'accesso diretto tramite URL reindirizza alla dashboard dipendente"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Restrizione Accesso Changelog"
                secondary="Il changelog è ora visibile solo agli amministratori. Gli utenti con ruolo 'employee' non possono più visualizzare le informazioni sulle versioni e le modifiche dell'applicazione"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          {/* v1.5.1 */}
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mb: 2 }}>
            Changelog v1.5.1 (02/01/2026)
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Fix Visualizzazione Validazioni per Super Admin"
                secondary="I super admin ora possono vedere tutte le validazioni presenze mensili, anche quelle di dipendenti appartenenti ad altri gruppi. Aumentato il limite di visualizzazione da 100 a 10000 validazioni"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Sezione 'Senza Gruppo' nella Vista Validazioni"
                secondary="Aggiunta sezione dedicata per visualizzare le validazioni dei dipendenti non assegnati a nessun gruppo nella vista organizzata per gruppi"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Fix Accesso Cartellino da Gestione Anomalie"
                secondary="Risolto il problema di accesso negato quando i super admin cliccano su 'Visualizza cartellino' dalla gestione anomalie. Il sistema ora riconosce correttamente i permessi super admin"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Filtro Gruppo per Super Admin"
                secondary="I super admin possono ora filtrare le validazioni per gruppo specifico selezionando un gruppo dal menu a tendina, mantenendo la possibilità di vedere tutte le validazioni quando nessun gruppo è selezionato"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Ricerca e Raggruppamento Dipendenti nel Cartellino"
                secondary="Sostituito il menu a tendina con Autocomplete per ricerca in tempo reale su nome, matricola, gruppo e dipartimento. I dipendenti sono ora raggruppati per gruppo di appartenenza con visualizzazione del nome gruppo"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Creazione Utenti senza Dipendente Associato"
                secondary="Aggiunto form completo in Gestione Utenti per creare utenti che non sono associati a un dipendente. Possibilità di abilitare immediatamente l'utente con generazione credenziali o crearlo disabilitato per abilitazione successiva"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          {/* v1.5.0 */}
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mb: 2 }}>
            Changelog v1.5.0 (30/12/2025)
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Calcolo Ore Attese Totali Mensili"
                secondary="Aggiunto calcolo e visualizzazione delle ore attese totali mensili nel preview di validazione per confronto con ore effettive"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Correzione Calcolo Anomalie con Tutte le Assenze"
                secondary="Il sistema ora considera tutte le tipologie di assenze (permesso + ferie + altre assenze) nel calcolo delle anomalie giornaliere e mensili"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Esclusione Dipendenti con Ore in Più dalle Anomalie"
                secondary="I dipendenti che hanno fatto più ore del previsto (straordinari) non vengono più mostrati nella lista anomalie mensili, con tolleranza di 0.5h"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Selettori Periodo nella Gestione Anomalie"
                secondary="Aggiunti selettori specifici per data (giornaliero), settimana (lunedì) e mese nella gestione anomalie con navigazione migliorata"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Default Mese Corrente nella Gestione Anomalie"
                secondary="La vista mensile delle anomalie mostra di default il mese corrente invece di un mese fisso, con selettore solo per mese (non giorno)"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Alert Anomalie Mese Precedente"
                secondary="Se ci sono anomalie per il mese precedente, viene mostrato un alert con pulsante 'Visualizza' per navigare direttamente a quel mese"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Chip 'Validato' nell'Elenco Dipendenti"
                secondary="Aggiunto chip verde 'Validato' accanto al nome del dipendente nella lista del cartellino admin quando il cartellino è validato per il mese selezionato"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Esportazione Massiva con File Separati"
                secondary="L'esportazione massiva genera un file Excel separato per ogni dipendente con nome formato 'codicefiscale - cognome nome.xlsx'"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Tabella Dipendenti Validati nell'Esportazione Massiva"
                secondary="Aggiunta tabella completa dei dipendenti validati in fondo alla pagina di esportazione massiva con evidenza del mese di riferimento, matricola, codice fiscale e stato (Validato/Provvisorio)"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Logica Anomalie Mensili Migliorata"
                secondary="Le anomalie giornaliere minime vengono ignorate se il totale mensile è corretto o positivo (entro tolleranza), evitando falsi positivi"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Correzione Visualizzazione Periodo in Tabella Anomalie"
                secondary="Corretto il bug che mostrava sempre '12/2025' nella colonna Periodo della tabella anomalie, ora mostra correttamente il periodo selezionato"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          {/* v1.4.0 */}
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mb: 2 }}>
            Changelog v1.4.0 (23/12/2025)
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Sistema di Validazione Cartellini Gerarchico"
                secondary="Validazione a cascata: dipendente (senza anomalie) → admin gruppo (con anomalie) → super admin. Validazione provvisoria fino all'ultimo giorno del mese"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Accesso come altro utente"
                secondary="Il super admin può aprire il profilo operativo di qualsiasi utente, con banner di notifica e possibilità di chiudere la sessione impersonata"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Gestione Anomalie Centralizzata"
                secondary="Nuova sezione dedicata per visualizzare e gestire anomalie giornaliere, settimanali e mensili con navigazione diretta al cartellino"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Rilevamento Anomalie Unificato"
                secondary="Logica centralizzata per il calcolo delle anomalie tra cartellino e gestione anomalie, garantendo sincronizzazione perfetta dei dati"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Validazione Solo Senza Anomalie"
                secondary="La validazione può essere effettuata solo se non ci sono anomalie. Se presenti, viene mostrato un pulsante per navigare al cartellino e correggerle"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Evidenza Soggetto nella Validazione"
                secondary="Nella pagina di validazione viene mostrato chiaramente il nome, cognome, matricola e sede del dipendente di cui si sta visualizzando il cartellino"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Navigazione Interattiva Cartellino"
                secondary="Dalla gestione anomalie è possibile navigare direttamente al cartellino del dipendente cliccando sulla riga o sul chip del periodo"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Gestione Festivi Italiani"
                secondary="Aggiunto l'8 dicembre (Immacolata Concezione) come festivo nel calendario italiano"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Controllo Accessi Basato su Gruppi"
                secondary="Gli admin di gruppo vedono solo i cartellini dei dipendenti assegnati al loro gruppo, mentre il super admin ha accesso completo"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Miglioramento Rilevamento Anomalie Piccole"
                secondary="Sistema migliorato per rilevare anche anomalie minime (es. 17 minuti) con tolleranza di arrotondamento e calcolo preciso"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Esclusione Giorno Corrente dalle Anomalie"
                secondary="Il giorno corrente non viene considerato per le anomalie se non è stato fatto il checkout"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Sincronizzazione Parametri URL"
                secondary="La pagina di validazione mantiene correttamente i parametri URL (user_id, year, month) per navigazione e aggiornamento"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          {/* v1.3.9 */}
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mb: 2 }}>
            Changelog v1.3.9 (10/12/2025)
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Fix Gestione NULL in Dipendenti Eliminati"
                secondary="Corretto errore scan per campi orario_inizio e orario_fine NULL nella sezione dipendenti eliminati"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Disabilitazione Automatica Utente"
                secondary="Quando si elimina un dipendente, l'utente associato viene automaticamente disabilitato"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Filtro Utenti Eliminati"
                secondary="Aggiunto filtro nella gestione utenti per filtrare utenti con dipendente eliminato nella tab Disabilitati"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Indicatore Dipendente Eliminato"
                secondary="Chip visivo 'Dipendente Eliminato' nella colonna Stato per identificare utenti con dipendente associato eliminato"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          {/* v1.3.8 */}
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mb: 2 }}>
            Changelog v1.3.8 (10/12/2025)
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Fix Paginazione Tabelle"
                secondary="Corretta la selezione 'righe per pagina' in Gestione Dipendenti, Gestione Utenti e modal Aggiungi Dipendenti Gruppo"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Invio Credenziali per Gruppi"
                secondary="Possibilità di inviare credenziali temporanee agli admin e dipendenti di un gruppo con dialog di conferma"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Dialog Conferma Invio Credenziali"
                secondary="Elenco completo destinatari con nome, email e tipo (Admin/Dipendente) prima dell'invio effettivo"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          {/* v1.3.7 */}
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mb: 2 }}>
            Changelog v1.3.7 (10/12/2025)
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Pannello Admin per Gruppi"
                secondary="Gli admin di gruppo possono accedere al pannello amministrativo dalla dashboard dipendente"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Creazione Admin Semplificata"
                secondary="Form semplificato per creare admin non dipendenti senza campi ore/giorni di lavoro"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Aggiornamento Automatico Ruolo"
                secondary="Il ruolo viene aggiornato automaticamente ad 'admin' quando si aggiunge un utente a un gruppo"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          {/* v1.3.6 */}
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mb: 2 }}>
            Changelog v1.3.6
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Filtri avanzati per aggiunta dipendenti ai gruppi"
                secondary="Sistema completo di filtri (Cognome, Nome, CF, Matricola, Sede, Ente, Stato, Regione, Tipo Sede, Ruolo) con tabella paginata"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Selezione multipla dipendenti"
                secondary="Possibilità di selezionare e aggiungere più dipendenti contemporaneamente ai gruppi con checkbox e contatore"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Gestione gruppi senza admin"
                secondary="Supporto completo per gruppi senza admin con indicatori visivi, alert informativi e conferma rimozione ultimo admin"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Campo ricerca dipartimento in Gestione Utenti"
                secondary="Aggiunto campo di ricerca per dipartimento nella tabella utenti, disponibile per utenti abilitati e disabilitati"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          {/* v1.3.5 */}
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mb: 2 }}>
            Changelog v1.3.5
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Fix conteggio dipendenti in permesso/ferie"
                secondary="La dashboard ora conta i dipendenti in permesso/ferie usando il calendario permessi del giorno corrente"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Correzione aggiornamento dipendenti con orari"
                secondary="Normalizzazione automatica degli orari (da timestamp ISO a formato HH:MM:SS) per evitare errori sul DB"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Migliorie gestione permessi lato dipendente"
                secondary="Aggiunto campo causale obbligatorio per richieste di permesso allineato al cartellino admin"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Fix pagina dipendenti eliminati"
                secondary="Utilizzo delle API tipizzate per caricare, ripristinare ed eliminare definitivamente i dipendenti eliminati"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          {/* v1.3.4 */}
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mb: 2 }}>
            Changelog v1.3.4
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Gestione Permessi nel Cartellino"
                secondary="Aggiunta funzionalità per gestire permessi/assenze direttamente dal cartellino admin"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Export Excel Formato INPAS"
                secondary="Nuovo formato di export Excel specifico per INPAS con formattazione avanzata"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Calcolo Ore Totali Migliorato"
                secondary="Ore totali giornaliere = ore lavorate + ore permesso con rilevamento anomalie"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Arrotondamento Matematico Ore"
                secondary="Arrotondamento matematico delle ore (6.98h → 7h) nel foglio Excel INPAS"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Limitazione Ore per Giorni Completi"
                secondary="Per giorni completi, le ore presenza sono limitate al valore previsto (senza ore extra)"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Struttura gruppi e deleghe"
                secondary="Sistema per organizzare gli amministratori in gruppi e delimitare il perimetro operativo sui dipendenti"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>
          
          <Divider sx={{ my: 2 }} />
          
          {/* v1.3.3 */}
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mb: 2 }}>
            Changelog v1.3.3 (27/11/2025)
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Validazione mensile presenze"
                secondary="Introdotto il nuovo flusso di validazione del cartellino con pulsante dedicato per dipendenti e amministratori."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Validazione mensile"
                secondary="La validazione è disponibile solo a mese completato. Per modifiche successive, revocare la validazione."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Preview riepilogo ore"
                secondary="Prima della conferma viene mostrato un riepilogo con ore totali, ferie/permessi, giorni lavorativi e note."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Export Excel cartellino"
                secondary="Nuovo download Excel con fogli Info Dipendente/Cartellino e riepilogo giornaliero dettagliato."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mb: 2 }}>
            Changelog v1.3.2 (09/11/2025)
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Evidenza cartellini irregolari"
                secondary="Nel menu amministratore i dipendenti con ore mancanti mostrano il chip “Irregolare” e il contatore dedicato."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Contatori sempre visibili"
                secondary="Utenti abilitati e cartellini irregolari sono ora riportati in testa al pannello del cartellino admin."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Gestione manuale presenze"
                secondary="Gli amministratori possono inserire o correggere orari anche senza check-in, con calcolo automatico ore e audit dettagliato."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Terminologia aggiornata"
                secondary="Lo stato “Da completare” è stato rinominato in “Irregolare” in tutta l’applicazione."
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mb: 2 }}>
            Changelog v1.3.1 (04/11/2025)
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText
                primary="Risoluzione pagina bianca dopo deploy"
                secondary="Implementato cache-busting per evitare problemi di cache browser dopo il deploy"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Card cliccabili nella dashboard"
                secondary="Le statistiche generali sono ora cliccabili per visualizzare l'elenco dei dipendenti per categoria"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Allineamento grafico Gestione Dipendenti"
                secondary="Interfaccia uniformata con Gestione Utenti: Container, Card, TablePagination, stili coerenti"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Nuovi filtri ricerca: Regione, Tipo Sede, Tipo Ruolo"
                secondary="Aggiunti tre dropdown per filtrare dipendenti per regione sede, tipo sede e tipo ruolo"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Icone Person colorate per stato"
                secondary="Icone nella tabella colorate: verde (attivo), rosso (inattivo), arancione (ferie), blu (malattia)"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Debounce ricerca aumentato a 1.5s"
                secondary="Maggior tempo per completare la digitazione prima dell'esecuzione della ricerca"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Risoluzione bug ricerca e paginazione"
                secondary="Corretti gli errori 500 nei filtri di ricerca e la paginazione in Gestione Dipendenti"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Risoluzione bug report"
                secondary="Corretto il bug nella sezione report e statistiche"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mb: 2, mt: 3 }}>
            Changelog v1.3 (03/11/2025)
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Gestione Checkout Dimenticati"
                secondary="Nuova sezione dashboard per visualizzare e gestire checkout dimenticati dai dipendenti"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Checkout Forzato Manuale"
                secondary="Possibilità per l'admin di forzare il checkout di un dipendente che ha dimenticato"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Checkout Automatico"
                secondary="Funzione per chiudere automaticamente tutte le presenze con più di 12 ore di lavoro"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Monitoraggio Anomalie Filtro Attivi"
                secondary="Le anomalie considerano solo dipendenti attivi per dati più precisi"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Report Regolari/Irregolari"
                secondary="Nuovo report con elenco separato di situazioni regolari e irregolari per data"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Quadratura ±10 Minuti"
                secondary="Sistema di tolleranza per orari completi con evidenza dell'arrotondamento applicato"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Evidenza Quadratura nei Report"
                secondary="Indicatori visivi per identificare quando è stata applicata quadratura (±10 min)"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mt: 2 }}>
            Changelog v1.2 (03/11/2025)
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Versioning Automatico"
                secondary="Generazione automatica versione con timestamp e commit hash ad ogni build"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Auto-Reload su Aggiornamento"
                secondary="L'app ricarica automaticamente quando è disponibile una nuova versione"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Statistiche Dashboard Ottimizzate"
                secondary="Le statistiche considerano solo utenti attivi per dati più accurati"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Gestione Cache Migliorata"
                secondary="Prevenzione problemi di cache dopo i deploy con header HTTP ottimizzati"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mt: 2 }}>
            Changelog v1.1 (02/11/2025)
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Gestione Utenti Completa"
                secondary="Nuova pagina con tab Utenti Abilitati/Disabilitati"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Invio Credenziali"
                secondary="Possibilità di inviare credenziali temporanee via email a utenti attivi"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Editing Inline"
                secondary="Modifica username, email e stato abilitazione direttamente dalla tabella"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Ricerca Avanzata"
                secondary="Filtri per username, nome, cognome ed email con icona di ricerca"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Paginazione"
                secondary="50 record per pagina con opzioni 10, 25, 50, 100"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Layout Ottimizzato"
                secondary="Tabelle responsive con header sticky e visualizzazione a schermo pieno"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="#0d47a1" sx={{ mt: 2 }}>
            Changelog v1.0 (Rilascio Iniziale)
          </Typography>
          <List dense>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Sistema Base"
                secondary="Check-in/check-out, gestione dipendenti, report, ferie e permessi"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Import Excel"
                secondary="Importazione dipendenti da file Excel con formato flessibile"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
            <ListItem sx={{ pl: 0 }}>
              <ListItemIcon><Chip label="✓" size="small" sx={{ minWidth: 'auto', p: 0 }} /></ListItemIcon>
              <ListItemText 
                primary="Password Reset"
                secondary="Recupero password via email con SendGrid"
                primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </ListItem>
          </List>
        </DialogContent>
          <DialogActions>
            <Button onClick={() => setChangelogOpen(false)} variant="contained" color="primary">
              Chiudi
            </Button>
          </DialogActions>
        </Dialog>
      )}

    </Box>
  );
};

// Helper function per ottenere la sezione corrente
const getCurrentSection = (pathname: string): string => {
  const sections: {[key: string]: string} = {
    '/dashboard': 'Dashboard Centrale',
    '/group-dashboard': 'Dashboard Gruppo',
    '/employee-dashboard': 'Area personale',
    '/mobile-presence': 'Timbratura Mobile',
    '/timesheet': 'Cartellino mensile',
    '/presence-validation': 'Validazioni presenze',
    '/leave': 'Ferie e Permessi',
    '/reports': 'Report e analisi',
    '/employees': 'Gestione dipendenti',
    '/employees/deleted': 'Dipendenti eliminati',
    '/users': 'Gestione utenti',
    '/admin-groups': 'Struttura gruppi e deleghe',
    '/guided-presence': 'Inserimento presenze gruppo',
    '/admin-timesheets': 'Cartellini del gruppo',
    '/timesheets-management': 'Gestione validazioni',
    '/timesheets-mass-export': 'Esportazione massiva cartellini',
    '/reports/monthly-schedule': 'Export presenze mensili',
    '/anomalies': 'Anomalie e controlli',
    '/leave/import': 'Import saldi ferie',
    '/leave/balances': 'Quadro saldi ferie',
    '/leave/history': 'Storico ferie e permessi',
    '/leave/reasons': 'Voci ferie e permessi',
    '/impersonation': 'Accesso come altro utente',
    '/announcements': 'Annunci globali',
  };

  return sections[pathname] || 'Dashboard';
};

export default Sidebar;
